import React from 'react';

import { ContentTypeClassification } from '@confluence/data-classification/entry-points/ContentTypeClassification';
import { type ContentClassificationMetadata } from '@confluence/data-classification/entry-points/useContentClassification';
import { ClassificationModalContextProvider } from '@confluence/data-classification/entry-points/ClassificationModalContext';
import { usePageContentId, useContentType, usePageSpaceKey } from '@confluence/page-context';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes';
import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';
import {
	CURRENT_STATUS,
	DRAFT_STATUS,
	ContentStatus,
} from '@confluence/page-utils/entry-points/enums';

import { useIsDisconnected } from '../hooks/useIsDisconnected';

import { AddClassification } from './AddClassification';

export type ContentClassifierProps = {
	contentClassificationMetadata: ContentClassificationMetadata;
	onRestoreFocus?: () => void;
	buttonTestId?: string;
};

export const ContentClassifier = ({
	contentClassificationMetadata,
	onRestoreFocus,
	buttonTestId,
}: ContentClassifierProps) => {
	const isDisconnected = useIsDisconnected();
	const [contentType] = useContentType();
	const [contentId] = usePageContentId();
	const [spaceKey] = usePageSpaceKey();
	const { contentStatus } = useContentStatus();

	const [canUpdateContent] = useCanPerformContentOperation({
		contentId: contentId ?? '',
		contentType,
		operation: OperationTypes.UPDATE,
		status: contentStatus === ContentStatus.DRAFT ? DRAFT_STATUS : CURRENT_STATUS,
	});

	const hasEditPermission = canUpdateContent ?? false;

	return contentClassificationMetadata.classification ? (
		<ContentTypeClassification
			spaceKey={spaceKey ?? ''}
			contentId={contentId ?? ''}
			userOffline={isDisconnected}
			readOnly={!hasEditPermission}
			contentType={contentType ?? ''}
			contentClassificationMetadata={contentClassificationMetadata}
			popupPlacementOverride="bottom-end"
			onRestoreFocus={onRestoreFocus}
			buttonTestId={buttonTestId}
			spacing="expanded"
		/>
	) : (
		<ClassificationModalContextProvider>
			<AddClassification
				contentClassificationMetadata={contentClassificationMetadata}
				hasEditPermission={hasEditPermission}
			/>
		</ClassificationModalContextProvider>
	);
};
